import type { PortfolioItem } from '@/types/portfolio'

const ID_RE = /^[a-z][a-z0-9-]*$/
/** 출시 전 항목이 문안에 반드시 밝혀야 하는 표기 — api `UPCOMING_PHRASE` 와 같은 값이에요. */
export const UPCOMING_PHRASE = '출시 예정'

export const CATEGORY_LABEL = { own: '자사 제품', client: '고객사 프로젝트' } as const
export const PLATFORM_LABEL = { web: '웹', app: '앱' } as const

/**
 * 저장 전 클라이언트 검증 — apps/api `PortfolioItem`·`PortfolioBody` 의 pydantic 규칙과 같은 내용이에요.
 * 서버도 같은 걸 다시 검증하니 여기 목적은 422 전에 사람이 읽을 메시지를 보여주는 것뿐이에요.
 */
export function validatePortfolioItems(items: PortfolioItem[]): string[] {
  const errors: string[] = []

  for (const item of items) {
    const name = item.name.trim() || item.id
    if (!ID_RE.test(item.id)) {
      errors.push(`"${item.id}": id 는 영소문자로 시작하고 영소문자·숫자·하이픈만 쓸 수 있어요.`)
    }
    if (item.name.trim() === '') errors.push(`"${item.id}": 서비스명이 비어 있어요.`)
    if (item.tagline.trim() === '') errors.push(`'${name}': 한 줄 소개가 비어 있어요.`)
    if (item.description.trim() === '') errors.push(`'${name}': 설명이 비어 있어요.`)
    if (item.platform.length === 0) errors.push(`'${name}': 플랫폼을 최소 하나 골라주세요.`)
    if (item.url && !/^https?:\/\//.test(item.url)) {
      errors.push(`'${name}': 링크는 http(s):// 로 시작해야 해요 — ${item.url}`)
    }
    for (const [label, value] of [
      ['커버 이미지', item.cover],
      ['로고', item.logo],
    ] as const) {
      // `/` 사이트 경로 또는 업로드가 돌려준 http(s) 절대 URL. api `_asset_must_be_path_or_url` 과 같은 규칙이에요.
      if (value && !/^(\/|https?:\/\/)/.test(value)) {
        errors.push(
          `'${name}': ${label} 는 \`/\` 로 시작하는 사이트 경로이거나 http(s):// URL 이어야 해요 — ${value}`,
        )
      }
    }

    // 플래그와 방문자가 읽는 문안은 한 몸이에요 — 어긋나면 화면은 '운영 중' 처럼 보이면서
    // 개수 KPI 만 조용히 줄어들어요(사실과 다른 광고가 돼요).
    const mentions = `${item.tagline} ${item.description}`.includes(UPCOMING_PHRASE)
    if (item.upcoming && !mentions) {
      errors.push(`'${name}': 출시 전 항목이면 문안에도 '${UPCOMING_PHRASE}' 을 밝혀야 해요.`)
    }
    if (mentions && !item.upcoming) {
      errors.push(`'${name}': 문안에 '${UPCOMING_PHRASE}' 이 있으면 '출시 전' 표시도 함께 세워야 해요.`)
    }
  }

  const ids = items.map((item) => item.id)
  const duplicated = [...new Set(ids.filter((id, i) => ids.indexOf(id) !== i))]
  if (duplicated.length > 0) errors.push(`id 가 중복됐어요 — ${duplicated.join(', ')}`)

  if (items.length === 0) errors.push('포트폴리오 항목은 최소 1개가 필요해요.')
  else if (items.every((item) => item.upcoming || item.hidden)) {
    // 숨김도 방문자에겐 삭제와 같은 결과라 함께 세요 — api `_needs_one_visible_live_item` 과 같은 규칙이에요.
    errors.push(
      "노출 중이면서 운영 중인 항목이 최소 1개 필요해요 — 0개면 About 지표가 '자사 서비스 0개' 가 돼요.",
    )
  }

  return errors
}
